import React, { useState } from "react";
import { useTemplateContext } from "../../../context/TemplateContext";
import SingleSelectBox from "../../../components/FormComponents/SingleSelectBox";
import Input from "../../../components/FormComponents/Input";
import Textarea from "../../../components/FormComponents/Textarea";
import Button from "../../../components/FormComponents/Button";
import axiosInstance, { handleApiError } from "../../../services/axiosService";
import { toastError, toastSuccess } from "../../../utils/toastUtils";
import RDBMSTemplateFields from "./components/RDBMSTemplateFields";
import useDirtyChecker from "../../../hooks/useDirtyChecker";
import GenericActionBar from "../../../Layouts/GenericActionBar/GenericActionBar";

const RdbmsTemplateEditor = ({ file, templateData, setTemplateData, dbConnectors }) => {
  const { setFieldsArr } = useTemplateContext();
  const [isSaving, setIsSaving] = useState(false);
  const { isDirty, markClean } = useDirtyChecker(templateData);

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const { name: _name, ...dataToSend } = templateData;

      const { message } = await axiosInstance.patch(`/${file.parentData}/${file.id}`, dataToSend);

      toastSuccess(message);
      markClean();
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangeTemplate = (e) => {
    if (e.target.name === "fields") {
      setFieldsArr(e.target.value);
    } else {
      setTemplateData({ ...templateData, [e.target.name]: e.target.value });
    }
  };

  const handleChangeDatabaseType = (v) => {
    setTemplateData({ ...templateData, database_type: v.id });
  };

  const handleConvert = async () => {
    try {
      const { data } = await axiosInstance.post("/mysql/RDBMSConversion", {
        query: templateData.sql_query
      });
      setFieldsArr(JSON.parse(data));

      const parsedData = JSON.parse(data);

      const formattedFields = parsedData.map((item) => ({
        name: item.name,
        type: item.type,
        index: null,
        sub_fields: null
      }));

      setTemplateData({ ...templateData, fields: formattedFields });
      setFieldsArr(formattedFields);

      toastSuccess("Conversion successful");
    } catch (error) {
      toastError("Error converting SQL query");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <GenericActionBar
        path={
          <div>
            <em>{file.parentData}</em> → <span>{file.name}</span>
          </div>
        }
        actions={[
          {
            key: "save",
            icon: "save",
            title: isSaving ? "Saving..." : "Save",
            onClick: isSaving ? undefined : handleSave
          }
        ]}
        options={{
          save: {
            className: `${isDirty ? "save_glowing" : ""} ${isSaving ? "disabled" : ""}`.trim()
          }
        }}
      />
      <div className="page_main_section">
        <div className="template_form_outer">
          <div className="builder_form">
            <div style={{ width: "320px" }}>
              <SingleSelectBox
                selectedData={templateData?.database_type || ""}
                options={[
                  {
                    id: "mysql",
                    name: "MySQL"
                  },
                  {
                    id: "postgres",
                    name: "PostgreSQL"
                  }
                ]}
                onChange={handleChangeDatabaseType}
                label="Select Database"
                placeholder="Select RDBMS Type"
              />
              <div style={{ marginTop: "16px" }}>
                <SingleSelectBox
                  options={dbConnectors.filter((each) => {
                    if (templateData?.database_type === "postgres") {
                      return each.database_type === "postgresql";
                    }
                    return each.database_type === templateData?.database_type;
                  })}
                  selectedData={templateData?.db_connector}
                  onChange={(v) => {
                    setTemplateData({ ...templateData, db_connector: v.id });
                  }}
                  label="Select Connector"
                  placeholder="Select Connector"
                />
              </div>
              <div style={{ marginTop: "16px" }}>
                <Input
                  label={"Table Name"}
                  value={templateData["table_name"]}
                  name={"table_name"}
                  onChange={handleChangeTemplate}
                />
              </div>
            </div>
            <RDBMSTemplateFields />
            <div className="flex_column gap_3">
              <Textarea
                label={"SQL Query"}
                value={templateData["sql_query"]}
                name={"sql_query"}
                onChange={handleChangeTemplate}
              />
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button variant="secondary" onClick={handleConvert}>
                  Convert
                </Button>
              </div>
              <div style={{ color: "#888", marginBottom: "40px" }}>
                Example :{" "}
                <textarea
                  style={{
                    width: "100%",
                    height: "220px",
                    padding: "8px",
                    boxSizing: "border-box",
                    resize: "vertical",
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                    border: "1px solid #ccc",
                    fontSize: "12px",
                    color: "#888",
                    outline: "none"
                  }}
                  value={`CREATE TABLE \`Project\`
                          (
                            \`project_id\`          int          NOT NULL AUTO_INCREMENT,
                            \`user_project_id\`     varchar(255) DEFAULT NULL,
                            \`name\`                varchar(255) NOT NULL,
                            \`client_id\`           int          DEFAULT NULL,
                            \`start_date\`          datetime     DEFAULT NULL,
                            \`end_date\`            datetime     DEFAULT NULL,
                            \`project_instance_id\` varchar(255) DEFAULT NULL,
                            \`status\`              varchar(255) DEFAULT NULL,
                            \`competency\`          json         DEFAULT NULL,
                            PRIMARY KEY (\`project_id\`),
                            KEY                     \`client_id\` (\`client_id\`),
                            CONSTRAINT \`Project_ibfk_1\` FOREIGN KEY (\`client_id\`) REFERENCES \`Client\` (\`client_id\`)
                          ) ENGINE=InnoDB AUTO_INCREMENT=224 DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_0900_ai_ci;`}
                  readOnly // Optional: if you don't want the user to edit it
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RdbmsTemplateEditor;
